import { useState, useMemo } from 'react';
import { DataTable, type DataTableColumn } from '../../_shared/DataTable';
import { CURRENCY, NUMBER, PERCENT } from '../../_shared/format';
import type { PerformancePersonaCohortRow } from '@/services/api';

export function PersonaCohortTable({
  rows,
  loading,
}: {
  rows: PerformancePersonaCohortRow[];
  loading?: boolean;
}) {
  const [sortField, setSortField] = useState<string>('customer_count');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const sorted = useMemo(() => {
    const copy = rows.slice();
    copy.sort((a, b) => {
      const aVal = (a as unknown as Record<string, unknown>)[sortField];
      const bVal = (b as unknown as Record<string, unknown>)[sortField];
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortDir === 'asc' ? aVal - bVal : bVal - aVal;
      }
      const aStr = String(aVal ?? '');
      const bStr = String(bVal ?? '');
      return sortDir === 'asc'
        ? aStr.localeCompare(bStr)
        : bStr.localeCompare(aStr);
    });
    return copy;
  }, [rows, sortField, sortDir]);

  const columns: DataTableColumn<PerformancePersonaCohortRow>[] = [
    {
      key: 'persona_name',
      label: 'Persona',
      sortable: true,
      render: (row) => (
        <span className="font-medium text-navy-900">{row.persona_name}</span>
      ),
    },
    {
      key: 'customer_count',
      label: 'Customers',
      sortable: true,
      align: 'right',
      width: '120px',
      render: (row) => NUMBER.format(row.customer_count),
    },
    {
      key: 'avg_ltv',
      label: 'Avg LTV',
      sortable: true,
      align: 'right',
      width: '110px',
      render: (row) => CURRENCY.format(row.avg_ltv),
    },
    {
      key: 'avg_order_value',
      label: 'AOV',
      sortable: true,
      align: 'right',
      width: '100px',
      render: (row) => CURRENCY.format(row.avg_order_value),
    },
    {
      key: 'total_orders',
      label: 'Avg Orders',
      sortable: true,
      align: 'right',
      width: '100px',
      render: (row) => row.total_orders.toFixed(1),
    },
    {
      key: 'demographics',
      label: 'Demographics',
      width: '180px',
      render: (row) => {
        const parts: string[] = [];
        if (row.avg_age) parts.push(`${row.avg_age.toFixed(0)} yrs`);
        if (row.pct_female !== null && row.pct_female !== undefined) {
          parts.push(`${PERCENT(row.pct_female, 0)} F`);
        }
        return parts.length ? (
          <span className="text-slate-500">{parts.join(' · ')}</span>
        ) : (
          <span className="text-slate-400">—</span>
        );
      },
    },
  ];

  return (
    <DataTable<PerformancePersonaCohortRow>
      columns={columns}
      rows={sorted}
      rowKey={(r) => r.persona_id}
      loading={loading}
      emptyText="No persona cohort data available."
      sortField={sortField}
      sortDir={sortDir}
      onSortChange={(f, d) => {
        setSortField(f);
        setSortDir(d);
      }}
    />
  );
}
